import { api, getQueue, mutate, type Ack } from './api'
import { buzz } from './haptic'
import { landFlash, reducedMotion } from './motion'
import { paidLook, paidMoment, type PaidWay } from './paid-moment'
import { lineDrawing, type Drawing } from './picture-tiles'
import { clear, dayLabel, el, loadError, money, parseMoney, toast } from './ui'
import { giveVisits } from './closeout'
import { CLOSE_OUT_FROM } from './close-out-rules'
import { businessSettings } from './business-settings'
import { currencySymbol } from './round-config'
import { getMe } from './me'
import { ownersDecided, ownersTotal, payeesOf, type OwnerPaySetting, type PartnerRow, type Payee, type TakeRow } from './owner-pay-rules'
import './close-out.css'

// Close out the day (0095; Liam, 29 Oct 2026: "A is great", "ship it add in animations that would be good aswell"; the
// drawing he picked, A v2: https://claude.ai/artifact/NcVyCnpM2jXdmgFotjrtA8; docs/plans/close-out-day/BUILD.md).
// #/close-out/YYYY-MM-DD, from the bottom of a day in the Diary or a day on Money. For each person who worked that day,
// numbered steps: their jobs (what was not done, put on another day), the cash they hand in, and their pay (what People
// says, typed over if different; Cash or Bank is the tap that says it is paid, as on Owed). The owner's own jobs are
// one step that adds their day total. Everything saves to the day's totals (day_ledger), which Money reads.
//
// Movements, from the movement set (docs/ANIMATION-STANDARD.md): the one tick on a step done (180ms); Owed's paid moment
// on a Cash or Bank tap (green spreads from the button, a tick, a buzz, the coin if switched on); the next step's ring
// glows once (landFlash, 450ms); "You keep" glows once when it changes; the last step done brings the one mark (400ms)
// and "<day> closed" with a success buzz. Nothing plays on opening the page, and nothing moves with reduced motion.

export interface CloseOutJob { id: number; name: string; street: string; done: boolean; pence: number; why: string | null }
export interface CloseOutPerson {
  worker: string
  is_owner: boolean
  facts: { job_count: number; done_count: number; not_done_count: number; work_pence: number; cash_expected_pence: number; jobs: CloseOutJob[] }
  pay: { kind: 'day' | 'commission'; amount: number; min?: number | null } | null
  suggested_pence: number | null
  ledger: LedgerRow | null
  finished: FinishedRow | null
  // Set on the phone: a day from before Close out the day (its pay and cash were settled the old way), and a first
  // save waiting for signal (nothing more is saved for them until it has gone, so two firsts never clash).
  old?: boolean
  waiting?: boolean
  // A partner who is not an owner (0097): paid by their share in the last step, not a worker's pay step.
  partner?: boolean
  // Two or more in one van (0096), worked out on the phone (vansOf): the van they were in, their share of its work.
  van?: { lead: string; row: CrewRow | null; people: string[] } | null
  share?: number // a whole percent of the van's work
  workShare?: number // their share of the van's work, in pence: what their day total's work done is
}
export interface CrewRow { id: number; version: number; lead: string; person: string; share_pct: number | null }
export interface LedgerRow { id: number; version: number; worker: string; gross_pence: number | null; worker_cut_pence: number | null; paid_at: string | null; paid_how: 'cash' | 'bank' | null; cash_in_at: string | null; pay_rule: string | null; pay_open_at?: string | null }
export interface FinishedRow { id: number; version: number; invoice_no: string | null; invoice_pence: number | null; invoice_paid_at: string | null; cash_pence: number | null; cash_expected_pence: number | null; cash_received_at: string | null; reopened_at: string | null }
export interface CloseOutPayload {
  date: string; alone: boolean; people: CloseOutPerson[]; unassigned: { id: number; version: number; name: string; amount_pence: number | null }[]; workers: string[]; crews?: CrewRow[]
  // How the owners get paid and what each took that day (0097): the last step, "Where the day's money goes".
  owner_pay?: OwnerPaySetting; partners?: PartnerRow[]; takes?: TakeRow[]
}

const lower = (s: string): string => s.trim().toLowerCase()

// Each van on the day: its lead (the name on its visits) and the people with them, each with their share of the van's
// work, a whole percent (a share set by the owner, else an equal share of what is left). Their pay follows their share:
// a day rate is still the day rate; a commission is their percent of their share. Someone in no van keeps their own
// work. Sets van, share, workShare and suggested_pence on each person.
export function vansOf(d: CloseOutPayload): void {
  const crews = d.crews ?? []
  for (const p of d.people) {
    const mine = crews.find((c) => lower(c.person) === lower(p.worker))
    if (!mine) { p.van = null; p.share = 100; p.workShare = p.facts.work_pence; continue }
    const rows = crews.filter((c) => lower(c.lead) === lower(mine.lead))
    const lead = d.people.find((x) => lower(x.worker) === lower(mine.lead))
    const work = lead?.facts.work_pence ?? 0
    const s = sharesOf(rows, work).get(lower(p.worker))!
    p.van = { lead: lead?.worker ?? mine.lead, row: mine, people: rows.map((c) => c.person) }
    p.share = Math.round(s.pct)
    p.workShare = s.pence
    if (p.pay) p.suggested_pence = commissionOf(p.pay, p.workShare)
  }
}
// Each person's share of a van's work: the owner's whole percent where set, the rest split evenly, the pence handed out
// so they add up to the van's work exactly (Astra's review: three equal thirds lost a penny each). The same rule as
// functions/lib/crews.ts sharesOf.
export function sharesOf(rows: readonly CrewRow[], work: number): Map<string, { pct: number; pence: number }> {
  const set = rows.filter((r) => r.share_pct !== null).reduce((n, r) => n + (r.share_pct ?? 0), 0)
  const unset = rows.filter((r) => r.share_pct === null).length
  const pcts = rows.map((r) => r.share_pct ?? (unset ? Math.max(0, 100 - set) / unset : 0))
  const total = pcts.reduce((n, x) => n + x, 0) || 1
  const raw = pcts.map((x) => (work * x) / total)
  const pence = raw.map(Math.floor)
  let left = work - pence.reduce((n, x) => n + x, 0)
  for (const i of raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0]).map(([, i]) => i)) {
    if (left <= 0) break
    pence[i]! += 1
    left -= 1
  }
  return new Map(rows.map((r, i) => [lower(r.person), { pct: (pcts[i]! / total) * 100, pence: pence[i]! }]))
}
// A person's pay for some work: the day rate, or their percent of it, at least their minimum on a day with work (0097).
// The same rule as functions/lib/day-close.ts invoiceAmount.
export function commissionOf(pay: NonNullable<CloseOutPerson['pay']>, work: number): number {
  if (pay.kind === 'day') return pay.amount
  const cut = Math.round((work * pay.amount) / 100)
  return work > 0 && pay.min ? Math.max(cut, pay.min) : cut
}
const inOthersVan = (p: CloseOutPerson): boolean => !!p.van && lower(p.van.lead) !== lower(p.worker)

export type StepKind = 'jobs' | 'cash' | 'pay' | 'own'
export interface Step { kind: StepKind; done: boolean }

// ---- the rules (no page: checked by scripts/close-out.test.mjs) ----------------------------------------------------

// Still to do: a visit that is neither done nor has a reason it was not.
export const stillToDo = (p: CloseOutPerson): CloseOutJob[] => p.facts.jobs.filter((j) => !j.done && !j.why)
export const notDone = (p: CloseOutPerson): CloseOutJob[] => p.facts.jobs.filter((j) => !j.done)
// The cash they took that day: what they said they hand over when they finished, else what Roundly has them taking.
export const cashTaken = (p: CloseOutPerson): number => p.finished?.cash_pence ?? p.facts.cash_expected_pence ?? 0
// A day before Close out the day with a day total was settled the old way: its pay and cash are not asked for again.
// Unless Money's Paid was tapped to say it was not paid after all (0117): then it is to pay, as anyone's.
export const settledBefore = (p: CloseOutPerson): boolean => !!p.old && (p.ledger?.worker_cut_pence ?? 0) > 0 && !p.ledger?.paid_at && !p.ledger?.pay_open_at
export const cashIn = (p: CloseOutPerson): boolean => !!(p.ledger?.cash_in_at || p.finished?.cash_received_at || (p.old && p.ledger))
// A day total whose pay was never put in is not paid, whatever is ticked, as Money reads it ("Pay not put in"; Astra).
export const isPaid = (p: CloseOutPerson): boolean => (!!(p.ledger?.paid_at || p.finished?.invoice_paid_at) && !(p.ledger && p.ledger.worker_cut_pence == null && !p.old)) || settledBefore(p)
// What to pay them: what was saved, else their invoice, else what People's pay comes to.
export function payDue(p: CloseOutPerson): number | null {
  // In a van, their pay is on their share: an invoice from Finish my day made before the van was set is not (Astra).
  if (p.van) return p.ledger?.worker_cut_pence ?? p.suggested_pence ?? null
  return p.ledger?.worker_cut_pence ?? p.finished?.invoice_pence ?? p.suggested_pence ?? null
}
export function payRule(p: CloseOutPerson): string | null {
  return p.pay ? `${p.pay.kind} ${p.pay.amount}${p.pay.kind === 'commission' && p.pay.min ? ` min ${p.pay.min}` : ''}` : null
}
// The owners' and partners' amounts for the day (0097): who was out, the day's work, and what the workers are paid (not
// counting anyone who is a partner, whose share is their pay).
export function payeesFor(d: CloseOutPayload): Payee[] {
  let work = 0, workerPay = 0
  for (const p of d.people) {
    work += p.facts.work_pence
    // A partner's own day total adds only the worker pay it already carries (none from Close out the day on), as Money.
    if (!p.is_owner) workerPay += p.partner ? 0 : (payDue(p) ?? 0)
  }
  // Out working: a job done, or in a van, as Money and the Diary read it (Astra). A day before Close out the day asks for
  // no owner's amount, whatever the setting is now; only what was saved shows.
  const people = d.people.map((p) => ({ worker: p.worker, is_owner: p.is_owner, out: p.facts.done_count > 0 || !!p.van }))
  if (d.date < CLOSE_OUT_FROM) return payeesOf(null, [], d.takes ?? [], { people, work, workerPay })
  return payeesOf(d.owner_pay, d.partners ?? [], d.takes ?? [], { people, work, workerPay })
}
export function stepsOf(p: CloseOutPerson): Step[] {
  if (p.is_owner || p.partner) return [{ kind: 'own', done: !!p.ledger }]
  // In another's van: their jobs and cash are the van's (on its lead's card); here, only their pay.
  if (inOthersVan(p)) return [{ kind: 'pay', done: isPaid(p) }]
  const steps: Step[] = [{ kind: 'jobs', done: stillToDo(p).length === 0 }]
  if (cashTaken(p) > 0) steps.push({ kind: 'cash', done: cashIn(p) })
  steps.push({ kind: 'pay', done: isPaid(p) })
  return steps
}
export function dayClosed(d: CloseOutPayload): boolean {
  return d.people.length > 0 && d.unassigned.length === 0 && d.people.every((p) => stepsOf(p).every((s) => s.done)) && ownersDecided(payeesFor(d))
}
// The day's figures: the work done, what has been paid out, what is still to pay, and what is left after pay. Pay not
// yet paid is still pay: "You keep" takes it off (Astra, 29 Oct 2026: after worker pay, paid or not).
// The owners' amounts (0097) come off too: what is left is what the business keeps.
export function totals(d: CloseOutPayload): { work: number; paidOut: number; toPay: number; owners: number; keep: number } {
  let work = 0, paidOut = 0, toPay = 0
  for (const p of d.people) {
    work += p.facts.work_pence
    if (p.is_owner) continue
    if (p.partner) continue
    const due = payDue(p) ?? 0
    if (isPaid(p)) paidOut += due
    else toPay += due
  }
  const owners = ownersTotal(payeesFor(d))
  return { work, paidOut, toPay, owners, keep: work - paidOut - toPay - owners }
}

// ---- the page ------------------------------------------------------------------------------------------------------

const first = (name: string): string => name.trim().split(/\s+/)[0] || name
const REASON_WORDS: Record<string, string> = { not_in: 'Not in', no_access: 'No access', weather: 'Weather', customer_cancelled: 'Customer cancelled', time: 'Ran out of time', other: 'Other' }

let data: CloseOutPayload | null = null
// The owners' amounts open to change on this page (0097), by name.
const editing = new Set<string>()
let root: HTMLElement | null = null
let busy = false
// Opened in place under its day on Money's Day totals (Liam, 30 Oct 2026, design B: "click into a tab and edit instead
// of close out"; https://claude.ai/artifact/RhfGhtvRMTTiwP5RdgBj46): the same close-out, its figures as picture tiles on
// top, so every save, check and Undo is this page's own (Astra's drawing check: no second way to save a day).
let inline: { owe: number; work: number; jobs: number; kept: number | null; saved?: () => void } | null = null
// Which opening of the page this is: a save or a read begun under an earlier one (another day opened meanwhile, on Money
// or by going to the page) finishes quietly, never drawing into the day open now (Astra's build review).
let mountId = 0
const current = (id: number): boolean => id === mountId && data !== null

export function mountCloseOut(date: string): void {
  const screen = document.querySelector<HTMLElement>('#screen')!
  root = el('div', { class: 'close-out' })
  screen.append(root)
  inline = null
  data = null
  busy = false
  editing.clear()
  void load(date, ++mountId)
}

// One day at a time: opening another, or this one again, starts afresh.
export function mountCloseOutInto(date: string, host: HTMLElement, opts: { owe: number; work: number; jobs: number; kept: number | null; saved?: () => void }): void {
  root = el('div', { class: 'close-out close-out-inline' })
  host.append(root)
  inline = opts
  data = null
  busy = false
  editing.clear()
  void load(date, ++mountId)
}

// Is this element still the close-out open now? Not once another day, or the Close out page, has opened since: Money
// then opens the day afresh rather than keep controls whose day is no longer the one in hand (Astra's re-check).
export function closeOutStillIn(host: HTMLElement): boolean {
  return !!inline && !!root && root.parentElement === host
}

// Money read itself again behind the open day: only the tiles take its figures; the cards below, with anything half
// typed in them, are left as they are.
export function updateCloseOutFigures(f: { owe: number; work: number; jobs: number; kept: number | null }): void {
  if (!inline) return
  Object.assign(inline, f)
  const d = data, host = root
  const old = host?.querySelector<HTMLElement>('.close-out-tiles')
  if (!d || !old) return
  const short = businessSettings().short_name || 'the business'
  const keeper = !d.owner_pay || d.owner_pay.mode === 'keep' ? 'You keep' : `Kept by ${short}`
  old.replaceWith(dayTiles(d, totals(d), payeesFor(d), keeper))
}

// The day's figures as tiles (design B): work done (into the Diary's day), each person and what they get, each owner's
// amount, what is kept, and what customers owe that day (into Owed). A tile takes you to the part of the close-out below
// that changes it; Kept works itself out.
function dayTiles(d: CloseOutPayload, t: ReturnType<typeof totals>, payees: Payee[], keeper: string): HTMLElement {
  const host = root
  const strip = el('div', { class: 'close-out-tiles', role: 'list' })
  const initials = (name: string): string => name.trim().split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || '?'
  const step = (key: string, exact = true): HTMLElement | null => [...(host?.querySelectorAll<HTMLElement>('.close-out-step') ?? [])]
    .find((r) => (exact ? r.getAttribute('data-step') === key : (r.getAttribute('data-step') ?? '').startsWith(key))) ?? null
  const go = (target: HTMLElement | null | undefined): void => {
    if (!target) return
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' })
    landFlash(target)
    target.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true })
  }
  const tile = (o: { face?: string; drawing?: Drawing; amount: string; words: string; due?: boolean; quiet?: boolean; href?: string; to?: () => HTMLElement | null | undefined }): HTMLElement => {
    const ring = o.face ? el('span', { class: 'close-out-tile-face', 'aria-hidden': 'true' }, o.face) : el('span', { class: 'close-out-tile-ring', 'aria-hidden': 'true' }, lineDrawing(o.drawing ?? 'pound', 22))
    const body = el('span', { class: 'close-out-tile-words' }, el('strong', { class: o.due ? 'is-due' : '' }, o.amount), el('span', {}, o.words))
    const cls = `close-out-tile${o.quiet ? ' is-quiet' : ''}`
    if (o.href) return el('a', { class: cls, href: o.href, role: 'listitem' }, ring, body)
    if (!o.to) return el('div', { class: cls, role: 'listitem' }, ring, body)
    const b = el('button', { type: 'button', class: cls, role: 'listitem' }, ring, body)
    b.addEventListener('click', () => go(o.to!()))
    return b
  }
  // Work done and Kept as Money's own row says them (its saved totals, work nobody was put on, older owners' amounts):
  // the tiles never disagree with the row they open from (Astra's build review).
  const work = inline ? inline.work : t.work
  strip.append(tile({ drawing: 'calendar', amount: money(work), words: `Work done · ${inline ? inline.jobs : d.people.reduce((n, p) => n + p.facts.done_count, 0)} jobs · in the Diary`, href: `#/schedule?from=${d.date}` }))
  if (d.unassigned.length) strip.append(tile({ drawing: 'people', amount: `${d.unassigned.length} job${d.unassigned.length === 1 ? '' : 's'}`, words: 'Nobody on them yet', due: true, to: () => host?.querySelector<HTMLElement>('.close-out-card.is-warn') }))
  d.people.forEach((p) => {
    if (p.is_owner || p.partner) return
    const due = payDue(p)
    const words = p.waiting ? `${first(p.worker)} · saving on this phone` : due === null ? `${first(p.worker)} · pay not put in` : `${first(p.worker)} · ${isPaid(p) ? 'paid' : 'to pay'}`
    strip.append(tile({ face: initials(p.worker), amount: due === null ? 'Not put in' : money(due), words, due: due === null || !isPaid(p), to: () => step(`${p.worker}|pay`) ?? step(`${p.worker}|`, false) }))
  })
  payees.forEach((p) => {
    const amount = p.take ? p.take.amount_pence : p.suggested
    strip.append(tile({ face: lower(p.name) === lower(getMe()?.workerName ?? '') ? 'YOU' : initials(p.name), amount: amount === null ? 'Not put in' : money(amount),
      words: `${whoSays(p.name)} · ${p.take ? (p.take.paid_at ? 'taken' : 'not taken yet') : 'to decide'}`, due: !p.take, to: () => step(`owner|${p.name}`) }))
  })
  // Not closed out yet: no figure until it is whole, as Money's own row (Astra's re-check: work nobody was put on).
  if (inline && inline.kept === null) strip.append(tile({ drawing: 'house', amount: 'Once closed out', words: keeper, quiet: true }))
  else strip.append(tile({ drawing: 'house', amount: money(inline && inline.kept !== null ? inline.kept : t.keep), words: `${keeper} · works itself out`, quiet: true }))
  if (inline && inline.owe > 0) strip.append(tile({ drawing: 'pound', amount: `${money(inline.owe)} owed`, words: 'Customers · in Owed', due: true, href: '#/owed' }))
  return strip
}

async function load(date: string, id: number = mountId): Promise<void> {
  const here = root
  if (!here || id !== mountId) return
  try {
    const d = await api<CloseOutPayload>(`/api/close-out/${date}`)
    if (root !== here || id !== mountId) return
    // A first save still waiting on this phone (kept across a reload): nothing more for them until it has gone.
    const waiting = getQueue().filter((q) => q.method === 'POST' && q.path === '/api/day-ledger')
      .map((q) => q.body as { date?: string; worker?: string })
    vansOf(d)
    const partnerNames = new Set((d.partners ?? []).map((x) => lower(x.name)))
    for (const p of d.people) {
      // A day total with worker pay on it (from before they were a partner, or not yet put in) stays a worker's, paid
      // and ticked as one (Astra's second re-check), as Money reads it.
      p.partner = !p.is_owner && d.owner_pay?.mode === 'split' && partnerNames.has(lower(p.worker)) && (!p.ledger || p.ledger.worker_cut_pence === 0)
      p.old = d.date < CLOSE_OUT_FROM
      p.waiting = !p.ledger && waiting.some((b) => b.date === d.date && (b.worker ?? '').trim().toLowerCase() === p.worker.trim().toLowerCase())
    }
    data = d
    draw()
    void keepWorkInStep(d)
  } catch (err) {
    if (root !== here) return
    clear(here)
    loadError(here, (err as Error).message, () => void load(date, id), undefined, 'This day')
  }
}

// A saved day total's work done follows the van as it is now (a rider in or out, a share moved) and the visits marked
// done since, so Money never counts a van twice (Astra's review). From 29 Oct 2026 only; never while saving.
async function keepWorkInStep(d: CloseOutPayload): Promise<void> {
  if (d.date < CLOSE_OUT_FROM) return
  const stale = d.people.filter((p) => p.ledger && !p.waiting && p.ledger.gross_pence !== (p.workShare ?? p.facts.work_pence))
  if (!stale.length || busy) return
  busy = true
  const id = mountId
  const failed: string[] = []
  for (const p of stale) {
    if (!current(id) || data !== d) return
    const ack = await saveLedger(p, {})
    if (!ack.ok && ack.status !== 0) failed.push(first(p.worker))
  }
  if (!current(id) || data !== d) return
  busy = false
  draw()
  // Never silent (Astra's re-check): the work done still disagrees with the van, so say so.
  if (failed.length) toast(`${failed.join(' and ')}'s work done did not update. Open this day again.`)
}

export function closeOutOnDataChanged(): void {
  if (data && !busy) void load(data.date)
}

function draw(fresh: { step?: string; finished?: boolean; keep?: boolean } = {}): void {
  const d = data
  const host = root
  if (!d || !host) return
  clear(host)
  const label = dayLabel(d.date)
  const t = totals(d)
  const closed = dayClosed(d)

  const left = el('div', { class: 'close-out-side' },
    el('a', { class: 'close-out-back', href: `#/schedule?from=${d.date}` }, `← ${label} in the Diary`),
    el('h1', {}, `Close out ${label}`))
  const payees = payeesFor(d)
  // Owners paid by a wage or a share (0097): what is left is the business's, not "You keep".
  // "Kept by <short name>", as Money says it (Liam: "is that consistent wording with the rest of the app?").
  const short = businessSettings().short_name || 'the business'
  const keeper = !d.owner_pay || d.owner_pay.mode === 'keep' ? 'You keep' : `Kept by ${short}`
  if (inline) {
    // In place on Money: the tiles, then each person's card and where the money goes, as on the page.
    const people = el('div', { class: 'close-out-people' })
    if (d.alone) people.append(el('p', { class: 'close-out-empty' }, 'You work alone, so there is nobody to pay.'))
    else if (!d.people.length && !d.unassigned.length) people.append(el('p', { class: 'close-out-empty' }, `Nobody has jobs on ${label}.`))
    if (d.unassigned.length) people.append(unassignedCard(d))
    for (const p of d.people) people.append(personCard(p, fresh.step))
    if (payees.length) people.append(moneyGoesCard(d, payees, t, fresh.step))
    host.append(
      el('div', { class: 'close-out-inline-head' },
        el('strong', {}, closed ? `${label} · closed out` : `${label} · not closed out yet`),
        el('span', {}, closed ? 'Tap a tile to change it. Each change saves as you make it.' : 'Filled in the way Close out fills it. Nothing is closed out until each step is ticked.'),
        el('a', { href: `#/close-out/${d.date}` }, 'Full Close out page')),
      dayTiles(d, t, payees, keeper), people)
    if (closed && fresh.finished) host.append(finishMark(label, true))
    return
  }
  const keepRow = el('div', { class: `close-out-sum-row is-keep${t.keep < 0 ? ' is-loss' : ''}` },
    el('span', {}, t.keep < 0 ? (keeper === 'You keep' ? 'The day lost' : `${short} is down`) : keeper), el('strong', {}, money(Math.abs(t.keep))))
  left.append(el('div', { class: 'close-out-sum' },
    el('div', { class: 'close-out-sum-row' }, el('span', {}, 'Work done'), el('strong', {}, money(t.work))),
    el('div', { class: 'close-out-sum-row' }, el('span', {}, 'Worker pay'), el('strong', {}, money(t.paidOut))),
    ...(t.toPay ? [el('div', { class: 'close-out-sum-row is-due' }, el('span', {}, 'Still to pay'), el('strong', {}, money(t.toPay)))] : []),
    ...(payees.length ? [el('div', { class: 'close-out-sum-row' }, el('span', {}, payees.length === 1 ? `${whoSays(payees[0]!.name)} took` : 'Owners took'), el('strong', {}, money(t.owners)))] : []),
    keepRow))
  left.append(el('p', { class: 'close-out-note' }, payees.length ? 'After worker pay and what the owners took, before fuel and kit. It goes in Money, Day totals.' : 'After worker pay, before fuel and kit. It goes in Money, Day totals.'))
  if (closed) left.append(finishMark(label, !!fresh.finished))

  const right = el('div', { class: 'close-out-people' })
  if (d.alone) right.append(el('p', { class: 'close-out-empty' }, 'You work alone, so there is nobody to pay. What you earned each day is at the bottom of the day in the Diary.'))
  else if (!d.people.length && !d.unassigned.length) right.append(el('p', { class: 'close-out-empty' }, `Nobody has jobs on ${label}.`))
  if (d.unassigned.length) right.append(unassignedCard(d))
  for (const p of d.people) right.append(personCard(p, fresh.step))
  if (payees.length) right.append(moneyGoesCard(d, payees, t, fresh.step))
  host.append(el('div', { class: 'close-out-grid' }, left, right))
  if (fresh.keep) landFlash(keepRow)

  // The next step to do glows once after a step is done (never on opening the page).
  if (fresh.step && !fresh.finished) {
    const next = host.querySelector<HTMLElement>('.close-out-step:not(.is-done) .close-out-ring')
    if (next) landFlash(next)
  }
}

function finishMark(label: string, now: boolean): HTMLElement {
  const mark = el('div', { class: `close-out-finish${now ? ' is-now' : ''}`, role: 'status' })
  const ring = el('span', { class: `close-out-mark${now && !reducedMotion() ? ' rd-mark' : ''}`, 'aria-hidden': 'true' }, lineDrawing('tick', 34))
  const burst = el('span', { class: 'close-out-dots', 'aria-hidden': 'true' })
  if (now && !reducedMotion()) for (const [dx, dy, green] of [[-50, -30, false], [48, -36, true], [50, 28, false], [-46, 34, true]] as const) burst.append(el('i', { style: `--dx:${dx}px;--dy:${dy}px`, class: green ? 'is-green' : '' }))
  mark.append(el('span', { class: 'close-out-mark-wrap' }, ring, burst), el('strong', {}, `${label} closed`), el('a', { href: '#/analysis' }, 'See it in Money'))
  return mark
}

function unassignedCard(d: CloseOutPayload): HTMLElement {
  const n = d.unassigned.length
  const card = el('section', { class: 'close-out-card is-warn', 'aria-label': 'Jobs with nobody on them' },
    el('h2', {}, `${n} job${n === 1 ? ' has' : 's have'} nobody on ${n === 1 ? 'it' : 'them'}`),
    el('p', {}, `${d.unassigned.map((u) => first(u.name)).join(', ')}. Give ${n === 1 ? 'it' : 'them'} to whoever did ${n === 1 ? 'it' : 'them'}, so the pay adds up.`))
  const row = el('div', { class: 'close-out-give' })
  for (const name of d.workers) {
    const b = el('button', { type: 'button', class: 'close-out-chip' }, name)
    b.addEventListener('click', () => {
      if (busy) return
      busy = true
      for (const x of row.querySelectorAll('button')) (x as HTMLButtonElement).disabled = true
      const id = mountId, tell = inline?.saved
      void giveVisits(d.unassigned.map((u) => ({ id: u.id, version: u.version } as never)), name).finally(() => { tell?.(); if (id === mountId) busy = false; void load(d.date, id) })
    })
    row.append(b)
  }
  card.append(row)
  return card
}

function personCard(p: CloseOutPerson, freshStep: string | undefined): HTMLElement {
  const steps = stepsOf(p)
  const left = steps.filter((s) => !s.done).length
  const name = p.is_owner ? 'You' : p.worker
  const leads = !!p.van && !inOthersVan(p)
  const card = el('section', { class: `close-out-card${p.van ? ' is-van' : ''}`, 'aria-label': p.is_owner ? 'Your jobs' : p.worker },
    el('div', { class: 'close-out-card-head' },
      el('h2', {}, leads ? (p.is_owner ? 'Your van' : `${p.worker}'s van`) : name),
      el('span', { class: left ? '' : 'is-done' }, left ? `${left} to do` : 'All done')))
  if (inOthersVan(p)) card.append(el('p', { class: 'close-out-van-line' }, `In ${first(p.van!.lead)}'s van · ${p.share}% of its work`))
  else if (p.facts.job_count > 0) card.append(vanChips(p))
  steps.forEach((s, i) => card.append(stepRow(p, s, i + 1, freshStep === `${p.worker}|${s.kind}`)))
  return card
}

// Who was in this person's van (0096; the drawing's TeamDesk): the people with them, each with an x to take them out,
// and the others who could have been, to tap in. Someone with jobs of their own that day has their own van.
function vanChips(p: CloseOutPerson): HTMLElement {
  const d = data!
  const box = el('div', { class: 'close-out-van' })
  const crew = (d.crews ?? []).filter((c) => lower(c.lead) === lower(p.worker) && lower(c.person) !== lower(p.worker))
  const busyNames = new Set([...(d.crews ?? []).map((c) => lower(c.person)), ...d.people.filter((x) => x.facts.job_count > 0).map((x) => lower(x.worker))])
  const others = d.workers.filter((w) => !busyNames.has(lower(w)))
  if (!crew.length && !others.length) return box
  box.append(el('span', { class: 'close-out-van-label' }, crew.length ? 'In the van' : `Anyone in the van with ${p.is_owner ? 'you' : first(p.worker)}?`))
  const row = el('div', { class: 'close-out-give' })
  for (const c of crew) {
    const b = el('button', { type: 'button', class: 'close-out-chip is-on', 'aria-label': `Take ${c.person} out of the van` }, `✓ ${c.person} ×`)
    b.addEventListener('click', () => void crewChange(b, { label: `${c.person} out of the van`, method: 'DELETE', path: `/api/day-crews/${c.id}`, body: { version: c.version } }))
    row.append(b)
  }
  for (const w of others) {
    const b = el('button', { type: 'button', class: 'close-out-chip', 'aria-label': `${w} was in the van` }, `+ ${w}`)
    b.addEventListener('click', () => void crewChange(b, { label: `${w} in ${p.worker}'s van`, method: 'POST', path: '/api/day-crews', body: { date: d.date, lead: p.worker, person: w } }))
    row.append(b)
  }
  box.append(row)
  return box
}

async function crewChange(button: HTMLElement, o: { label: string; method: 'POST' | 'PATCH' | 'DELETE'; path: string; body: unknown }): Promise<void> {
  if (busy) return
  busy = true
  ;(button as HTMLButtonElement).disabled = true
  buzz('light')
  const id = mountId, date = data!.date, tell = inline?.saved
  const ack = await mutate(o)
  if (!refused(ack)) tell?.()
  if (id !== mountId) return
  busy = false
  void load(date, id)
}

function stepRow(p: CloseOutPerson, s: Step, n: number, fresh: boolean): HTMLElement {
  const row = el('div', { class: `close-out-step${s.done ? ' is-done' : ''}`, 'data-step': `${p.worker}|${s.kind}` })
  const ring = el('span', { class: `close-out-ring${s.done && fresh && !reducedMotion() ? ' rd-tick' : ''}`, 'aria-hidden': 'true' }, s.done ? lineDrawing('tick', 16) : String(n))
  const body = el('div', { class: 'close-out-step-body' })
  row.append(ring, body)
  const head = (title: string, amount = ''): HTMLElement => el('div', { class: 'close-out-step-head' }, el('strong', {}, title), amount ? el('strong', {}, amount) : '')
  const line = (text: string, cls = ''): HTMLElement => el('span', { class: `close-out-line${cls ? ` ${cls}` : ''}` }, text)

  if (s.kind === 'own') {
    body.append(head(inOthersVan(p) ? 'Your share' : 'Your jobs', money(p.workShare ?? p.facts.work_pence)),
      line(inOthersVan(p) ? `${p.share}% of ${first(p.van!.lead)}'s van${s.done ? ' · in Day totals' : ''}` : `${p.facts.done_count} of ${p.facts.job_count} done${p.van ? ` · your ${p.share}% share` : ''}${s.done ? ' · in Day totals' : ''}`))
    if (!s.done) {
      const add = el('button', { type: 'button', class: 'close-out-go' }, 'Add to Day totals')
      add.addEventListener('click', () => void save(p, {}, `${p.worker}|own`, add))
      body.append(add)
    }
    return row
  }
  if (s.kind === 'jobs') {
    const todo = stillToDo(p), missed = notDone(p).filter((j) => j.why)
    body.append(head('Jobs'), line(`${p.facts.done_count} of ${p.facts.job_count} done${missed.length ? ` · ${missed.map((j) => `${first(j.name)}: ${REASON_WORDS[j.why!] ?? 'Other'}`).join(', ')}` : ''}`))
    if (todo.length) body.append(line(`${todo.length} still to do: ${todo.map((j) => first(j.name)).join(', ')}`, 'is-warn'))
    if (missed.length || todo.length) {
      const ids = [...missed, ...todo].map((j) => j.id)
      const move = el('button', { type: 'button', class: 'close-out-soft' }, ids.length === 1 ? `Put ${first(([...missed, ...todo][0]!).name)} on another day` : `Put these ${ids.length} on another day`)
      move.addEventListener('click', () => {
        void import('./screens/schedule').then(({ focusDiary }) => { focusDiary(ids, data!.date, 'Drag each onto another day, or use Move.', false); window.location.hash = '#/schedule' })
      })
      body.append(move)
    }
    return row
  }
  if (s.kind === 'cash') {
    const cash = cashTaken(p)
    body.append(head('Cash handed in', money(cash)), line(s.done ? `${first(p.worker)} handed it in` : `Roundly has ${first(p.worker)} taking ${money(cash)} in cash`, s.done ? 'is-good' : ''))
    if (s.done && !cashIn({ ...p, old: false })) return row // settled before Close out the day: nothing to undo
    const b = el('button', { type: 'button', class: s.done ? 'close-out-soft' : 'close-out-go' }, s.done ? 'Undo' : 'Handed in')
    b.addEventListener('click', () => void saveCash(p, !s.done, `${p.worker}|cash`, b))
    body.append(b)
    return row
  }
  // Pay.
  const due = payDue(p)
  if (s.done) {
    if (settledBefore(p)) {
      body.append(head(`Pay ${first(p.worker)}`, money(due)), line('Settled before Close out the day', 'is-good'))
      return row
    }
    body.append(head(`Pay ${first(p.worker)}`, money(due)), line(`Paid ${money(due)}${p.ledger?.paid_how ? ` by ${p.ledger.paid_how}` : ''}`, 'is-good'))
    // Paid something other than their invoice: said, never settled quietly (Astra's build review).
    if (p.finished?.invoice_pence != null && due !== null && due !== p.finished.invoice_pence) body.append(line(`Their invoice ${p.finished.invoice_no ?? ''} was ${money(p.finished.invoice_pence)}: ${due > p.finished.invoice_pence ? `${money(due - p.finished.invoice_pence)} more` : `${money(p.finished.invoice_pence - due)} less`} paid.`.replace('  ', ' '), 'is-warn'))
    const undo = el('button', { type: 'button', class: 'close-out-soft' }, 'Undo')
    undo.addEventListener('click', () => void savePay(p, null, null, `${p.worker}|pay`, undo))
    body.append(undo)
    return row
  }
  const vanWords = p.van ? ` of their ${p.share}% share of the van, ${money(p.workShare ?? 0)}` : ` of ${money(p.facts.work_pence)}`
  const rule = p.ledger?.worker_cut_pence != null ? 'What you put for them' : !p.van && p.finished?.invoice_pence != null ? `${first(p.worker)}'s invoice ${p.finished.invoice_no ?? ''}`.trim()
    : p.pay ? (p.pay.kind === 'day' ? `Day rate ${money(p.pay.amount)}, from People` : `${p.pay.amount}%${vanWords}, from People`) : 'No pay set on People'
  body.append(head(`Pay ${first(p.worker)}`, due !== null ? money(due) : ''), line(`${rule}. Change it if you paid something else.`))
  const input = el('input', { class: 'close-out-amount', inputmode: 'decimal', 'aria-label': `How much you paid ${p.worker}`, value: due !== null ? (due / 100).toFixed(2) : '', placeholder: '0.00' }) as HTMLInputElement
  const ways = el('div', { class: 'close-out-ways' }, el('label', { class: 'close-out-amount-label' }, el('span', {}, `Paid them ${currencySymbol()}`), input))
  for (const way of ['cash', 'bank'] as PaidWay[]) {
    const b = el('button', { type: 'button', class: 'close-out-way' }, way === 'cash' ? 'Cash' : 'Bank')
    paidLook(b, way)
    b.addEventListener('click', () => {
      let pence: number | null
      try { pence = parseMoney(input.value) } catch (err) { toast((err as Error).message); return }
      if (pence === null) { toast(`Type what you paid ${first(p.worker)} first`); input.focus(); return }
      void savePay(p, pence, way, `${p.worker}|pay`, b, row)
    })
    ways.append(b)
  }
  // On commission in a van: their share of its work, in tens.
  const crewRow = p.van?.row
  if (crewRow && p.pay?.kind === 'commission' && p.ledger?.worker_cut_pence == null) {
    const share = p.share ?? 0
    const step = (to: number): HTMLElement => {
      const b = el('button', { type: 'button', class: 'close-out-share-btn', 'aria-label': to < share ? 'Less' : 'More' }, to < share ? '−' : '+')
      b.addEventListener('click', () => void crewChange(b, { label: `${p.worker}'s share`, method: 'PATCH', path: `/api/day-crews/${crewRow.id}`, body: { version: crewRow.version, share_pct: to } }))
      // Never more than the van, with the shares others were given by hand (the server refuses it too).
      const othersSet = (data!.crews ?? []).filter((c) => lower(c.lead) === lower(p.van!.lead) && c.id !== crewRow.id).reduce((n, c) => n + (c.share_pct ?? 0), 0)
      if (to < 10 || to > 100 - othersSet) (b as HTMLButtonElement).disabled = true
      return b
    }
    body.append(el('div', { class: 'close-out-share' }, el('span', {}, 'Their share of the van'), step(share - 10), el('strong', {}, `${share}%`), step(share + 10)))
  }
  body.append(ways, line(`Tap how you paid ${first(p.worker)}, as on Owed.`, 'is-hint'))
  return row
}

// ---- the last step: where the day's money goes (0097; design F1) -----------------------------------------------------

// "You" for the signed-in owner, else their first name.
function whoSays(name: string): string { return lower(name) === lower(getMe()?.workerName ?? '') ? 'You' : first(name) }

function moneyGoesCard(d: CloseOutPayload, payees: Payee[], t: ReturnType<typeof totals>, freshStep: string | undefined): HTMLElement {
  const left = payees.filter((p) => !p.take).length
  const card = el('section', { class: 'close-out-card close-out-goes', 'aria-label': 'Where the day’s money goes' },
    el('div', { class: 'close-out-card-head' }, el('h2', {}, `Where the day’s ${money(t.work)} goes`), el('span', { class: left ? '' : 'is-done' }, left ? `${left} to do` : 'All done')))
  // The bar: workers, the owners, what the business keeps.
  const whole = Math.max(1, t.work)
  const part = (pence: number, cls: string, label: string): HTMLElement => el('span', { class: `close-out-goes-part ${cls}`, style: `flex-basis:${Math.max(0, (pence / whole) * 100).toFixed(1)}%`, title: label })
  card.append(el('div', { class: 'close-out-goes-bar', 'aria-hidden': 'true' }, part(t.paidOut + t.toPay, 'is-workers', 'Worker pay'), part(t.owners, 'is-owners', 'Owners'), part(Math.max(0, t.keep), 'is-keep', 'Kept')))
  if (t.paidOut + t.toPay) card.append(el('p', { class: 'close-out-line' }, `Worker pay ${money(t.paidOut + t.toPay)}, on each person's card above.`))
  payees.forEach((p, i) => card.append(payeeRow(d, p, i + 1, freshStep === `owner|${p.name}`)))
  return card
}

function payeeRow(d: CloseOutPayload, p: Payee, n: number, fresh: boolean): HTMLElement {
  const done = !!p.take
  const row = el('div', { class: `close-out-step${done ? ' is-done' : ''}`, 'data-step': `owner|${p.name}` })
  const ring = el('span', { class: `close-out-ring${done && fresh && !reducedMotion() ? ' rd-tick' : ''}`, 'aria-hidden': 'true' }, done ? lineDrawing('tick', 16) : String(n))
  const body = el('div', { class: 'close-out-step-body' })
  row.append(ring, body)
  const who = whoSays(p.name)
  const title = p.category === 'wage' ? `${who === 'You' ? 'Your' : `${who}'s`} wage` : p.category === 'took' ? `What ${who === 'You' ? 'you' : who} took` : `${who === 'You' ? 'Your' : `${who}'s`} share`
  const amount = p.take?.amount_pence ?? p.suggested
  body.append(el('div', { class: 'close-out-step-head' }, el('strong', {}, title), amount !== null ? el('strong', {}, money(amount)) : ''))
  // Saved on this phone, waiting for signal: nothing more for it until it has gone, so it is never sent twice (Astra).
  const queued = getQueue().some((q) => q.path.startsWith('/api/owner-takes') && ((q.body as { period?: string; payee?: string } | undefined)?.period === d.date
    && lower((q.body as { payee?: string }).payee ?? '') === lower(p.name) || (!!p.take && q.path === `/api/owner-takes/${p.take.id}`)))
  if (queued) { body.append(el('span', { class: 'close-out-line' }, 'Saved on this phone. It sends when there is signal.')); return row }
  const t = p.take
  if (t && !editing.has(lower(p.name))) {
    body.append(el('span', { class: 'close-out-line is-good' }, t.paid_at ? `Took ${money(t.amount_pence)}${t.paid_how ? ` by ${t.paid_how}` : ''}` : `${money(t.amount_pence)} · not taken yet`))
    // Change opens the amount again, the saved one kept until a new one is saved (Astra: it was taken off first).
    const change = el('button', { type: 'button', class: 'close-out-soft' }, 'Change')
    change.addEventListener('click', () => { editing.add(lower(p.name)); draw() })
    body.append(change)
    return row
  }
  body.append(el('span', { class: 'close-out-line' }, t ? `Saved ${money(t.amount_pence)}. Type the new amount.` : `${p.why}${p.suggested !== null ? '. Change it if you took something else.' : ''}`))
  const start = t?.amount_pence ?? p.suggested
  const input = el('input', { class: 'close-out-amount', inputmode: 'decimal', 'aria-label': `${title} for the day`, value: start !== null ? (start / 100).toFixed(2) : '', placeholder: '0.00' }) as HTMLInputElement
  const ways = el('div', { class: 'close-out-ways' }, el('label', { class: 'close-out-amount-label' }, el('span', {}, who === 'You' ? `You took ${currencySymbol()}` : `${who} took ${currencySymbol()}`), input))
  const go = (way: PaidWay | null, button: HTMLElement): void => {
    let pence: number | null
    try { pence = parseMoney(input.value) } catch (err) { toast((err as Error).message); return }
    if (pence === null) { toast('Type the amount first'); input.focus(); return }
    const undo = way ? paidMoment({ host: row, from: button, way, mark: ring }) : () => {}
    const paid = { paid: !!way, ...(way ? { paid_how: way } : {}) }
    void takeChange(d, button, t
      ? { label: `${p.name} took ${money(pence)}`, method: 'PATCH', path: `/api/owner-takes/${t.id}`, body: { version: t.version, amount_pence: pence, ...paid } }
      : { label: `${p.name} took ${money(pence)}`, method: 'POST', path: '/api/owner-takes', body: { period: d.date, payee: p.name, category: p.category, amount_pence: pence, rule: p.rule, ...paid } }, p.name, undo)
  }
  for (const way of ['cash', 'bank'] as PaidWay[]) {
    const b = el('button', { type: 'button', class: 'close-out-way' }, way === 'cash' ? 'Cash' : 'Bank')
    paidLook(b, way)
    b.addEventListener('click', () => go(way, b))
    ways.append(b)
  }
  const later = el('button', { type: 'button', class: 'close-out-soft' }, 'Not taken yet')
  later.addEventListener('click', () => go(null, later))
  ways.append(later)
  body.append(ways)
  if (t) {
    // Changing it: keep it as it was, or take it off altogether.
    const keep = el('button', { type: 'button', class: 'close-out-soft' }, 'Keep it as it was')
    keep.addEventListener('click', () => { editing.delete(lower(p.name)); draw() })
    const off = el('button', { type: 'button', class: 'link-button' }, 'Take it off')
    off.addEventListener('click', () => void takeChange(d, off, { label: `Take off ${p.name}'s amount`, method: 'DELETE', path: `/api/owner-takes/${t.id}`, body: { version: t.version } }, p.name, null))
    body.append(el('div', { class: 'close-out-ways' }, keep, off))
  }
  return row
}

async function takeChange(d: CloseOutPayload, button: HTMLElement, o: { label: string; method: 'POST' | 'PATCH' | 'DELETE'; path: string; body: unknown }, name: string, undo: (() => void) | null): Promise<void> {
  if (busy) return
  busy = true
  ;(button as HTMLButtonElement).disabled = true
  buzz('light')
  const wasClosed = dayClosed(d)
  const id = mountId, tell = inline?.saved
  const ack = await mutate<TakeRow>(o)
  if (!current(id) || data !== d) { if (!refused(ack)) tell?.(); return }
  busy = false
  if (refused(ack)) { undo?.(); void load(d.date, id); return }
  if (ack.status === 0) { void load(d.date, id); return }
  editing.delete(lower(name))
  d.takes = (d.takes ?? []).filter((t) => lower(t.payee) !== lower(name))
  if (o.method !== 'DELETE' && ack.row) d.takes.push(ack.row)
  const redraw = (): void => { if (current(id)) after(o.method !== 'DELETE' ? `owner|${name}` : undefined, wasClosed, true) }
  if (undo && !reducedMotion()) window.setTimeout(redraw, 320)
  else redraw()
}

// ---- saving ----------------------------------------------------------------------------------------------------------

// The person's day total: made the first time anything of theirs is saved (the work done from their visits), changed
// after. The work done follows their visits each time, so a visit marked done since is counted.
async function saveLedger(p: CloseOutPerson, change: Record<string, unknown>): Promise<Ack<LedgerRow>> {
  const d = data!
  // Their first save is still on this phone: a second would be a second new total, refused when the signal comes back.
  if (!p.ledger && p.waiting) return { ok: false, status: -1, error: `${first(p.worker)}'s first tick is saved on this phone. Tick the rest once it has sent.` }
  // An owner's or partner's day total carries no worker pay (0097: a partner's was left unknown, so Money kept it open).
  const base = { gross_pence: p.workShare ?? p.facts.work_pence, ...((p.is_owner || p.partner) && !p.ledger ? { worker_cut_pence: 0 } : {}), ...change }
  const ack = p.ledger
    ? await mutate<LedgerRow>({ label: `Close out ${d.date} (${p.worker})`, method: 'PATCH', path: `/api/day-ledger/${p.ledger.id}`, body: { version: p.ledger.version, ...base } })
    : await mutate<LedgerRow>({ label: `Close out ${d.date} (${p.worker})`, method: 'POST', path: '/api/day-ledger', body: { date: d.date, worker: p.worker, ...base } })
  if (ack.ok && ack.row) p.ledger = ack.row
  if (!ack.ok && ack.status === 0 && !p.ledger) p.waiting = true
  return ack
}

// Their Finish my day follows their day total on the server (functions/lib/close-out-sync.ts); the page's copy follows too.
function finishedFollows(p: CloseOutPerson, change: { invoice_paid_at?: string | null; cash_received_at?: string | null }): void {
  if (!p.finished || p.finished.reopened_at) return
  if ('cash_received_at' in change) p.finished.cash_received_at = change.cash_received_at ? (p.finished.cash_received_at ?? change.cash_received_at) : null
  if ('invoice_paid_at' in change && p.finished.invoice_no) p.finished.invoice_paid_at = change.invoice_paid_at ? (p.finished.invoice_paid_at ?? change.invoice_paid_at) : null
}

function refused(ack: Ack): boolean {
  if (ack.ok) return false
  if (ack.status === -1) { toast(ack.error ?? 'Waiting for signal.'); return true }
  if (ack.status === 0) { toast('Saved on this phone. It sends when there is signal.'); return false }
  toast(ack.error ?? 'Did not save. Try again.')
  return true
}

async function save(p: CloseOutPerson, change: Record<string, unknown>, step: string, button: HTMLElement): Promise<void> {
  if (busy) return
  busy = true
  ;(button as HTMLButtonElement).disabled = true
  buzz('light')
  const wasClosed = dayClosed(data!)
  const id = mountId, date = data!.date, tell = inline?.saved
  const ack = await saveLedger(p, change)
  if (!current(id)) { if (!refused(ack)) tell?.(); return }
  busy = false
  if (refused(ack)) { void load(date, id); return }
  after(step, wasClosed, false)
}

async function saveCash(p: CloseOutPerson, on: boolean, step: string, button: HTMLElement): Promise<void> {
  if (busy) return
  busy = true
  ;(button as HTMLButtonElement).disabled = true
  if (on) buzz('light')
  const wasClosed = dayClosed(data!)
  const id = mountId, date = data!.date, tell = inline?.saved
  const ack = await saveLedger(p, { cash_in_at: on })
  if (!current(id)) { if (!refused(ack)) tell?.(); return }
  busy = false
  if (refused(ack)) { void load(date, id); return }
  finishedFollows(p, { cash_received_at: on ? 'now' : null })
  if (ack.status === 0 && p.ledger) p.ledger.cash_in_at = on ? 'now' : null
  after(on ? step : undefined, wasClosed, false)
}

// Paid (way) or not paid (null). The paid moment plays from the tap, before the answer, and is taken back if refused.
async function savePay(p: CloseOutPerson, pence: number | null, way: PaidWay | null, step: string, button: HTMLElement, row?: HTMLElement): Promise<void> {
  if (busy) return
  busy = true
  ;(button as HTMLButtonElement).disabled = true
  const undo = way && row ? paidMoment({ host: row, from: button, way, mark: row.querySelector<HTMLElement>('.close-out-ring') }) : () => {}
  const wasClosed = dayClosed(data!)
  const change: Record<string, unknown> = way
    ? { worker_cut_pence: pence, paid_at: true, paid_how: way, pay_rule: payRule(p) }
    // An older day taken back off paid stays to pay, never settled the old way again (0117).
    : { paid_at: false, paid_how: null, ...(p.old ? { pay_open_at: true } : {}) }
  const id = mountId, date = data!.date, tell = inline?.saved
  const ack = await saveLedger(p, change)
  if (!current(id)) { if (refused(ack)) undo(); else tell?.(); return }
  if (refused(ack)) { busy = false; undo(); void load(date, id); return }
  if (ack.status === 0 && p.ledger) Object.assign(p.ledger, { paid_at: way ? 'now' : null, paid_how: way, worker_cut_pence: pence ?? p.ledger.worker_cut_pence, ...(!way && p.old ? { pay_open_at: 'now' } : {}) })
  // Their invoice from Finish my day is the same pay: paid together, and unpaid together (on the server too).
  finishedFollows(p, { invoice_paid_at: way ? 'now' : null })
  busy = false
  // The moment is under way on the row; the page is drawn again once it has spread (300ms), never waiting on the save.
  const redraw = (): void => { if (current(id)) after(way ? step : undefined, wasClosed, true) }
  if (way && !reducedMotion()) window.setTimeout(redraw, 320)
  else redraw()
}

function after(step: string | undefined, wasClosed: boolean, keep: boolean): void {
  if (!data) return
  const d = data
  const nowClosed = dayClosed(d)
  const finished = !wasClosed && nowClosed
  if (finished) buzz('success')
  draw({ step, finished, keep })
  window.dispatchEvent(new CustomEvent('roundly:close-out-saved', { detail: { date: d.date } }))
  // In place on Money: its row, month and totals read again behind the open day (Astra's build review).
  inline?.saved?.()
}
